/**
 * Metafield Products Loader
 * Carica prodotti correlati basati su metafield usando Storefront API
 */

class MetafieldProductsLoader {
  constructor(container) {
    this.container = container;
    this.config = {
      namespace: container.dataset.namespace,
      key: container.dataset.key,
      value: container.dataset.value,
      apiToken: container.dataset.apiToken,
      maxProducts: parseInt(container.dataset.maxProducts) || 8,
      shopDomain: window.Shopify.shop
    };
    
    this.currentProductHandle = window.location.pathname.split('/').filter(Boolean).pop();
    this.init();
  }

  async init() {
    try {
      const matchingProducts = await this.fetchProductsWithImages();
      
      if (matchingProducts.length > 0) {
        await this.renderProducts(matchingProducts.slice(0, this.config.maxProducts));
      } else {
        this.showNoResults();
      }
    } catch (error) {
      console.error('Error loading metafield products:', error);
      this.showError(error);
    }
  }

  async fetchProductsWithImages() {
    let allProducts = [];
    let hasNextPage = true;
    let cursor = null;
    let pageCount = 0;
    const maxPages = 20;
    const searchValue = this.config.value.trim().toLowerCase();
    let foundCount = 0;

    while (hasNextPage && pageCount < maxPages && foundCount < this.config.maxProducts + 10) {
      pageCount++;

      const query = `
        query getProducts${cursor ? '($cursor: String!)' : ''} {
          products(first: 50${cursor ? ', after: $cursor' : ''}) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                handle
                title
                availableForSale
                featuredImage {
                  url
                  altText
                }
                metafield(namespace: "${this.config.namespace}", key: "${this.config.key}") {
                  value
                }
              }
            }
          }
        }
      `;

      const variables = cursor ? { cursor } : {};
      const apiUrl = `https://${this.config.shopDomain}/api/2024-01/graphql.json`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.config.apiToken
        },
        body: JSON.stringify({ query, variables })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.errors) {
        console.error('GraphQL errors:', data.errors);
        throw new Error('GraphQL query failed');
      }

      const products = data.data.products.edges.map(edge => edge.node);
      
      const matching = products.filter(product => {
        if (!product.metafield || !product.metafield.value) {
          return false;
        }
        
        if (product.handle === this.currentProductHandle) {
          return false;
        }
        
        const productValue = product.metafield.value.trim().toLowerCase();
        return productValue === searchValue;
      });

      allProducts.push(...matching);
      foundCount += matching.length;

      hasNextPage = data.data.products.pageInfo.hasNextPage;
      cursor = data.data.products.pageInfo.endCursor;
      
      if (foundCount >= this.config.maxProducts + 5) {
        break;
      }
      
      if (!hasNextPage) {
        break;
      }
    }

    return allProducts;
  }

  async renderProducts(products) {
    this.addStyles();
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      const grid = document.createElement('div');
      grid.className = 'product-grid-minimal';
      
      products.forEach(product => {
        const card = this.createSimpleCard(product);
        grid.appendChild(card);
      });
      
      this.container.innerHTML = '';
      this.container.appendChild(grid);
    } catch (error) {
      console.error('Error rendering products:', error);
      this.showError(error);
    }
  }

  createSimpleCard(product) {
    const link = document.createElement('a');
    link.href = `/products/${product.handle}`;
    link.className = 'product-card-minimal';
    link.title = product.title;
    
    const imgContainer = document.createElement('div');
    imgContainer.style.cssText = 'position: relative; width: 100%; padding-bottom: 100%; overflow: hidden; background: #f5f5f5;';
    
    if (product.featuredImage) {
      const img = document.createElement('img');
      img.src = product.featuredImage.url + '&width=400';
      img.alt = product.title;
      img.loading = 'lazy';
      img.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover;';
      imgContainer.appendChild(img);
    }
    
    link.appendChild(imgContainer);
    return link;
  }

  addStyles() {
    if (document.getElementById('metafield-products-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'metafield-products-styles';
    style.textContent = `
      .product-grid-minimal {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        gap: 15px;
        max-width: 650px;
        padding: 10px 0;
      }
      
      .product-card-minimal {
        display: block;
        position: relative;
        overflow: hidden;
        border-radius: 8px;
        border: 1px solid #e0e0e0;
        background: #fff;
        box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }
      
      .product-card-minimal:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0,0,0,0.15);
        border-color: #ccc;
        z-index: 1;
      }
      
      .product-card-minimal img {
        transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      }
      
      .product-card-minimal:hover img {
        transform: scale(1.08);
      }
      
      @media (max-width: 989px) {
        .product-grid-minimal {
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          max-width: 100%;
        }
        
        .product-card-minimal {
          border-radius: 6px;
        }
      }
      
      @media (max-width: 640px) {
        .product-grid-minimal {
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
          padding: 8px 0;
        }
        
        .product-card-minimal {
          border-radius: 6px;
          border-width: 0.5px;
        }
        
        .product-card-minimal:hover {
          transform: translateY(-2px);
        }
      }
      
      @media (max-width: 400px) {
        .product-grid-minimal {
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
        }
      }
    `;
    document.head.appendChild(style);
  }

  showNoResults() {
    // Nasconde il container
    this.container.style.display = 'none';
    
    // Cerca il titolo "Related Products" o simili nelle vicinanze
    const parentSection = this.container.closest('.page-width, section, [class*="related"], [class*="recommend"]');
    
    if (parentSection) {
      // Nasconde tutta la sezione parent
      parentSection.style.display = 'none';
    } else {
      // Se non trova una sezione parent, cerca titoli nei fratelli precedenti
      let sibling = this.container.previousElementSibling;
      while (sibling) {
        if (sibling.matches('h2, h3, h4, .section-title, [class*="title"], [class*="heading"]')) {
          sibling.style.display = 'none';
          break;
        }
        sibling = sibling.previousElementSibling;
      }
    }
  }

  showError(error) {
    this.container.innerHTML = `
      <div class="text-center" style="padding: 40px 0; color: #d32f2f;">
        <p>Errore nel caricamento dei prodotti.</p>
        <small style="display: block; margin-top: 10px; font-size: 12px;">${error.message}</small>
      </div>
    `;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('[data-metafield-products-ajax]');
  
  if (containers.length > 0) {
    containers.forEach(container => {
      new MetafieldProductsLoader(container);
    });
  }
});